import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef, useState, useCallback } from 'react';
const VideoTile = ({ stream, peerId, username, showUsername, isLocal, streamType, blurAmount = 0, isAuthenticated = false, localCameraEnabled = false }) => {
    const videoRef = useRef(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(true); // CHANGED: Always start muted (was: !isLocal)
    const [autoplayEnabled, setAutoplayEnabled] = useState(false);
    const [streamAssigned, setStreamAssigned] = useState(false);
    console.log(`ðŸŽ¬ VideoTile render for ${isLocal ? 'local' : peerId}:`, {
        username,
        showUsername,
        streamType,
        isLocal,
        streamTracks: stream?.getTracks().length || 0,
        hasStream: !!stream,
        streamAssigned
    });
    const handleUserInteraction = useCallback(() => {
        console.log('ðŸ‘† User interaction detected, enabling autoplay');
        setAutoplayEnabled(true);
        // Force play attempt on user interaction
        if (videoRef.current && streamAssigned) {
            videoRef.current.play().catch((error) => {
                console.log(`Play failed after interaction for ${isLocal ? 'local' : peerId}:`, error);
            });
        }
    }, [isLocal, peerId, streamAssigned]);
    const handleVideoClick = useCallback(() => {
        if (!videoRef.current)
            return;
        handleUserInteraction();
        // REMOVED: Mute toggle removed - will add host controls in admin panel later
        // (Previously allowed local users to toggle mute)
    }, [handleUserInteraction]);
    // FIXED: Enhanced stream setup with proper audio-only stream handling
    useEffect(() => {
        if (!videoRef.current || !stream) {
            setStreamAssigned(false);
            return;
        }
        console.log(`ðŸŽ¥ Setting up video element for ${isLocal ? 'local' : peerId} (${streamType})`);
        console.log('ðŸ“Š Stream tracks:', stream.getTracks().map(track => ({
            kind: track.kind,
            label: track.label,
            enabled: track.enabled,
            readyState: track.readyState,
            muted: track.muted
        })));
        const videoElement = videoRef.current;
        // FIXED: Check for video tracks with better audio-only stream handling
        const videoTracks = stream.getVideoTracks();
        const audioTracks = stream.getAudioTracks();
        if (videoTracks.length === 0) {
            // FIXED: Only skip rendering for REMOTE audio-only streams
            // Local streams always render (even with errors) so user sees their camera status
            if (!isLocal && audioTracks.length > 0) {
                console.log(`â„¹ï¸ Skipping remote audio-only stream: ${peerId} (${audioTracks.length} audio tracks)`);
                setStreamAssigned(false);
                setHasError(false);
                return;
            }
            else {
                // Local stream missing video tracks - show error instead of hiding
                console.log(`âš ï¸ No video tracks available for ${isLocal ? 'local' : peerId}`);
                setHasError(true);
                return;
            }
        }
        const videoTrack = videoTracks[0];
        console.log(`ðŸŽ¯ Video track details for ${isLocal ? 'local' : peerId}:`, {
            readyState: videoTrack.readyState,
            enabled: videoTrack.enabled,
            muted: videoTrack.muted,
            label: videoTrack.label,
            id: videoTrack.id
        });
        // FIXED: Simplified track readiness check for MediaSoup compatibility
        if (!isLocal && (!videoTrack.enabled || videoTrack.readyState === 'ended')) {
            console.log(`â³ Track not ready for ${peerId}, readyState: ${videoTrack.readyState}, enabled: ${videoTrack.enabled}`);
            // Set a reasonable timeout instead of infinite waiting
            const timeoutId = setTimeout(() => {
                console.log(`âš¡ Timeout reached, proceeding with setup for ${peerId}`);
                setupVideo();
            }, 2000); // Wait max 2 seconds
            const checkTrackReady = () => {
                console.log(`ðŸ” Track state check for ${peerId}: ${videoTrack.readyState}, enabled: ${videoTrack.enabled}`);
                if (videoTrack.enabled && videoTrack.readyState !== 'ended') {
                    console.log(`âœ… Remote track is ready for ${peerId}, setting up video`);
                    clearTimeout(timeoutId);
                    setupVideo();
                }
                else if (videoTrack.readyState !== 'ended') {
                    // Check again in 100ms if not ended
                    setTimeout(checkTrackReady, 100);
                }
            };
            checkTrackReady();
            return;
        }
        // For local streams or ready remote streams, setup immediately
        setupVideo();
        function setupVideo() {
            if (!videoRef.current || !stream)
                return;
            console.log(`ðŸ”§ Setting up video element for ${isLocal ? 'local' : peerId}`);
            // Direct assignment with better error handling
            try {
                videoElement.srcObject = stream;
                videoElement.muted = true; // CHANGED: Force all videos muted (was: !isLocal)
                setStreamAssigned(true);
                setHasError(false); // Clear any previous errors
                console.log(`âœ… Stream assigned to video element for ${isLocal ? 'local' : peerId}`);
            }
            catch (error) {
                console.error(`âŒ Failed to assign stream for ${isLocal ? 'local' : peerId}:`, error);
                setHasError(true);
                return;
            }
            // Event handlers with proper sequence
            const handleLoadStart = () => {
                console.log(`ðŸ“„ Video load start for ${isLocal ? 'local' : peerId}`);
                setIsLoading(true);
                setHasError(false);
            };
            const handleLoadedMetadata = () => {
                console.log(`ðŸŽ¥ Video metadata loaded for ${isLocal ? 'local' : peerId}`);
                setIsLoading(false);
                // Try autoplay after metadata is loaded
                if (videoElement.readyState >= 1) { // HAVE_METADATA
                    const playPromise = videoElement.play();
                    if (playPromise !== undefined) {
                        playPromise
                            .then(() => {
                            console.log(`â–¶ï¸ Video autoplay succeeded for ${isLocal ? 'local' : peerId}`);
                            setAutoplayEnabled(true);
                        })
                            .catch(() => {
                            console.log(`âš ï¸ Video autoplay failed for ${isLocal ? 'local' : peerId}, requires user interaction`);
                            setAutoplayEnabled(false);
                        });
                    }
                }
            };
            const handleCanPlay = () => {
                console.log(`âœ… Video can play for ${isLocal ? 'local' : peerId}`);
                setIsLoading(false);
            };
            const handlePlay = () => {
                console.log(`â–¶ï¸ Video playing for ${isLocal ? 'local' : peerId}`);
                setIsPlaying(true);
                setIsLoading(false);
            };
            const handlePause = () => {
                console.log(`â¸ï¸ Video paused for ${isLocal ? 'local' : peerId}`);
                setIsPlaying(false);
            };
            const handleError = (e) => {
                console.error(`âŒ Video error for ${isLocal ? 'local' : peerId}:`, e);
                setHasError(true);
                setIsLoading(false);
            };
            const handleWaiting = () => {
                console.log(`â³ Video waiting for data for ${isLocal ? 'local' : peerId}`);
                setIsLoading(true);
            };
            const handleCanPlayThrough = () => {
                console.log(`ðŸš€ Video can play through for ${isLocal ? 'local' : peerId}`);
                setIsLoading(false);
            };
            // Add comprehensive event listeners
            videoElement.addEventListener('loadstart', handleLoadStart);
            videoElement.addEventListener('loadedmetadata', handleLoadedMetadata);
            videoElement.addEventListener('canplay', handleCanPlay);
            videoElement.addEventListener('canplaythrough', handleCanPlayThrough);
            videoElement.addEventListener('play', handlePlay);
            videoElement.addEventListener('pause', handlePause);
            videoElement.addEventListener('waiting', handleWaiting);
            videoElement.addEventListener('error', handleError);
            // Cleanup function
            return () => {
                if (videoElement) {
                    videoElement.removeEventListener('loadstart', handleLoadStart);
                    videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
                    videoElement.removeEventListener('canplay', handleCanPlay);
                    videoElement.removeEventListener('canplaythrough', handleCanPlayThrough);
                    videoElement.removeEventListener('play', handlePlay);
                    videoElement.removeEventListener('pause', handlePause);
                    videoElement.removeEventListener('waiting', handleWaiting);
                    videoElement.removeEventListener('error', handleError);
                }
            };
        }
    }, [stream, isLocal, peerId, streamType]);
    // Update mute state
    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.muted = isMuted;
        }
    }, [isMuted]);
    const renderVideoContent = () => {
        if (isLoading) {
            return (_jsxs("div", { className: "video-loading", children: [_jsx("div", { className: "video-loading-spinner" }), _jsx("div", { children: "Loading video..." }), !isLocal && !streamAssigned && (_jsx("div", { style: { fontSize: '12px', marginTop: '8px' }, children: "Waiting for stream..." }))] }));
        }
        if (hasError) {
            return (_jsxs("div", { className: "video-error", children: [_jsx("div", { children: "\u00F0\u0178\u201D\u00B9" }), _jsx("div", { children: "Video Error" }), _jsx("small", { children: "Failed to load video stream" })] }));
        }
        if (!autoplayEnabled && !isLocal && streamAssigned) {
            return (_jsx("div", { style: {
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(0, 0, 0, 0.8)',
                    color: 'white',
                    cursor: 'pointer',
                    zIndex: 2
                }, onClick: handleUserInteraction, children: _jsxs("div", { style: { textAlign: 'center' }, children: [_jsx("div", { style: { fontSize: '24px', marginBottom: '10px' }, children: "\u00E2\u2013\u00B6\u00EF\u00B8\u008F" }), _jsx("div", { children: "Click to play" })] }) }));
        }
        return null;
    };
    console.log(`ðŸ·ï¸ VideoTile ${isLocal ? 'local' : peerId}: username="${username}" showUsername=${showUsername}`);
    return (_jsxs("div", { className: `video-tile ${isLocal ? 'local' : 'remote'} ${streamType}`, onClick: handleVideoClick, children: [_jsx("video", { ref: videoRef, className: "video-element", playsInline: true, autoPlay: true, muted: true, style: {
                    filter: blurAmount > 0
                        ? `blur(${blurAmount * 0.2}px)`
                        : (!isAuthenticated && !isLocal
                            ? 'blur(5px)' // 25% blur (~5px) for unauthenticated users viewing others
                            : (isAuthenticated && !localCameraEnabled && !isLocal
                                ? 'blur(2.4px)' // 12% blur (~2.4px) for authenticated users with camera off
                                : 'none'))
                } }), renderVideoContent(), showUsername && username && isAuthenticated && (_jsx("div", { className: "username-overlay", children: _jsxs("span", { className: "username-text", children: [isLocal ? 'ðŸ‘¤' : 'ðŸ‘¥', " ", username] }) })), _jsxs("div", { style: {
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    zIndex: 3
                }, children: [isLoading && (_jsx("div", { style: {
                            background: 'rgba(255, 193, 7, 0.9)',
                            color: 'white',
                            padding: '4px 6px',
                            borderRadius: '50%',
                            fontSize: '12px'
                        }, children: "\u00E2\u008F\u00B3" })), !isPlaying && !isLoading && !hasError && streamAssigned && (_jsx("div", { style: {
                            background: 'rgba(156, 39, 176, 0.9)',
                            color: 'white',
                            padding: '4px 6px',
                            borderRadius: '50%',
                            fontSize: '12px'
                        }, children: "\u00E2\u008F\u00B8\u00EF\u00B8\u008F" })), hasError && (_jsx("div", { style: {
                            background: 'rgba(244, 67, 54, 0.9)',
                            color: 'white',
                            padding: '4px 6px',
                            borderRadius: '50%',
                            fontSize: '12px'
                        }, children: "\u00E2\u009D\u0152" }))] })] }));
};
export default VideoTile;
